'use client';

import { useEffect, useState } from 'react';
import {
  BarChart3,
  BellRing,
  BookOpen,
  Calendar,
  ChevronDown,
  Clock,
  FileText,
  FolderOpen,
  Gavel,
  Home,
  Inbox,
  Pencil,
  PenLine,
  Plus,
  Scale,
  Share2,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { menuVisible, type MenuItem } from '@/lib/menu';
import { cn } from '@/lib/utils';
import type { Sesion } from './Login';

// Mapa nombre (menu.ts) -> icono lucide. Reemplaza el antiguo Icon.tsx.
const ICONS: Record<string, LucideIcon> = {
  home: Home,
  users: Users,
  folder: FolderOpen,
  book: BookOpen,
  clock: Clock,
  plus: Plus,
  pencil: Pencil,
  pen: PenLine,
  gavel: Gavel,
  scale: Scale,
  inbox: Inbox,
  share: Share2,
  calendar: Calendar,
  chart: BarChart3,
  list: FileText,
  bell: BellRing,
};

function ItemIcon({ name, className }: { name: string; className?: string }) {
  const Cmp = ICONS[name] ?? FileText;
  return <Cmp className={className} />;
}

// ¿Qué sección (id del grupo) contiene la vista activa? Para abrirla por defecto.
function seccionDe(items: MenuItem[], vista: string): string | null {
  for (const m of items) {
    if (m.submenu?.some((s) => s.id === vista)) return m.id;
  }
  return null;
}

// Menú lateral verde. Se filtra según el rol del usuario (RBAC en la UI) y las
// secciones con submenú son DESPLEGABLES (clic en el encabezado para abrir/cerrar).
export default function Sidebar({
  usuario,
  vista,
  onNavegar,
}: {
  usuario: Sesion;
  vista: string;
  onNavegar: (v: string) => void;
}) {
  const items = menuVisible(usuario.rol);

  // Secciones abiertas. Por defecto se abre la que contiene la vista activa.
  const [abiertas, setAbiertas] = useState<Set<string>>(() => {
    const s = seccionDe(items, vista);
    return new Set(s ? [s] : []);
  });

  // Al navegar a una vista de otra sección, ábrela automáticamente.
  useEffect(() => {
    const s = seccionDe(items, vista);
    if (s) setAbiertas((prev) => (prev.has(s) ? prev : new Set(prev).add(s)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista]);

  function toggle(id: string) {
    setAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <nav className="flex h-full w-64 flex-col gap-1 overflow-y-auto bg-sidebar p-4 text-sidebar-foreground">
      <div className="mb-4 flex items-center gap-3 px-2">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
          <Scale className="size-5" />
        </span>
        <span className="text-sm font-semibold leading-tight">
          Módulo Disciplinario
          <br />
          <span className="text-xs font-normal text-sidebar-foreground/70">
            Sistema de Gestión Disciplinaria
          </span>
        </span>
      </div>

      {items.map((m) => {
        // Ítem sin submenú: botón directo.
        if (!m.submenu) {
          const activo = vista === m.id;
          return (
            <button
              key={m.id}
              onClick={() => onNavegar(m.id)}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                activo
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <ItemIcon name={m.icon} className="size-4 shrink-0" />
              {m.label}
            </button>
          );
        }

        // Sección con submenú DESPLEGABLE.
        const abierta = abiertas.has(m.id);
        // Marca el encabezado si la vista activa cuelga de esta sección (aunque esté cerrada).
        const contieneActiva = m.submenu.some((s) => s.id === vista);
        return (
          <div key={m.id} className="mt-1">
            <button
              type="button"
              onClick={() => toggle(m.id)}
              aria-expanded={abierta}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                contieneActiva && !abierta
                  ? 'text-sidebar-foreground'
                  : 'text-sidebar-foreground/90 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <ItemIcon name={m.icon} className="size-4 shrink-0" />
              <span className="flex-1 text-left">{m.label}</span>
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  abierta ? 'rotate-0' : '-rotate-90',
                )}
              />
            </button>

            {abierta && (
              <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
                {m.submenu.map((s) => {
                  const activo = vista === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => onNavegar(s.id)}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        activo
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      )}
                    >
                      <ItemIcon name={s.icon} className="size-4 shrink-0" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
